import React, { useEffect, useRef } from 'react';
import { shallowEqual, useDispatch, useSelector } from 'react-redux';
import { goToPlayerWithId } from 'containers/EPG/actions/channelBlocker/blocker.actions';
import ClaroUrlBuilder from 'utils/ClaroUrlBuilder';
import Utils from 'utils/nav/Utils';
import { getPurchaseButtonInfoForEPGTransactions } from 'action-creators/payway/purchaseButtonInfo';
import { showEpgTransactions } from 'containers/Player/actions/epgTransactions/epgTransactions.actions';
import { canPlay } from 'containers/Player/utils/PlayRules';
import { selectMetadataKeyByRegion } from 'reducers/selectors/metadata';
import getFirstNode from 'utils/getFirstNode';
import { GetRegion, LOCAL_STORAGE_USERHASH, PUSHTOHISTORY } from '../../../constants';
import { nodeSelected, previousNodeSelected } from '../actions/menu.actions';
import Logger from 'services/logger';
import {
  getNavData,
  getNodeBehavior,
  getSubNodes,
} from '../selectors/navData.selector';
import { isNodeMyContent } from '../../../utils/Utils';
import { findNestedNode } from '../../../utils/nodes/findNestedNode';
import { filterNodes } from '../utils/filterNodes';
import { useLocation, useNavigate } from 'react-router-dom';
import { getRegion } from 'utils/Region';

export const useMenuRedirect = () => {
  const navigate = useNavigate();
  const dispatch = useDispatch();
  const nodes = useSelector(getNavData, shallowEqual);
  const nodesSelected = useSelector(getSubNodes);
  const behaviorSelected = useSelector(getNodeBehavior);
  const infinite = useSelector(state => state.linealChannel?.data);
  const epgLineup = useSelector(state => state?.EpgLineUp?.data);
  const epgLineupRef = useRef(epgLineup);
  const resizingRef = useRef(false);
  const firstNode = getFirstNode();
  const { pathname, search } = useLocation();
  const user = useSelector(store => store.user);

  // Ref que siempre tiene el valor más reciente de epgLineup
  useEffect(
    () => {
      epgLineupRef.current = epgLineup;
    },
    [epgLineup],
  );

   // Ref que siempre tiene el valor más reciente de epgLineup
  useEffect(() => {
    epgLineupRef.current = epgLineup;
  }, [epgLineup]);

  const handleRedirect = ({ node }, location) => {
    const { code } = node || { code: firstNode };
    const url = `/${GetRegion()}/${code}`;
    let appBehavior = node?.app_behaviour || '{}';
    appBehavior = JSON.parse(appBehavior);
    if (appBehavior?.layout === 'guidechannels') {
      navigateToGuideChannels(node);
    } else {
      navigate(url, { ...location });
    }
    if (node?.level === 1)
      window.sessionStorage.setItem('prevSelected', node?.code);
    setNodeSelected(node, appBehavior?.layout);
  };
  const setNodeSelected = (node, appBehavior) => {
    if (appBehavior === 'playandgo' || appBehavior === 'filterbygenre') {
      if (
        filterNodes(nodes)?.find(
          currentNode => currentNode.code === nodesSelected.code,
        )
      ) {
        dispatch(previousNodeSelected(nodesSelected));
      } else {
        dispatch(previousNodeSelected({}));
      }
      return;
    }
    if (appBehavior !== 'guidechannels') dispatch(nodeSelected(node));
  };

  const setInitialNode = nodeCode => {
    try {
      if (!nodeCode) return;
      if (nodesSelected?.code === nodeCode) return;

      const nodesObject = nodes?.reduce((acc, node) => {
        acc[node.code] = node;
        return acc;
      }, {});

      let initialNode = nodesObject?.[nodeCode];

      if (!initialNode) {
        //  si no se encuentra el nodo dentro del primer nivel, intenta con los childs
        initialNode = findNestedNode(nodes, 'code', nodeCode);
      }
      if (!initialNode) initialNode = nodes?.[0]?.code;
      dispatch(nodeSelected(initialNode));
    } catch (error) {
      Logger.warn(error);
    }
  };

  const getTypeSubmenu = () => {
    const { childs } = nodesSelected;

    if (behaviorSelected === null) {
      return 'CAROUSEL';
    }

    if (behaviorSelected?.node_config?.show_node && childs) {
      if (isNodeMyContent(nodesSelected?.code)) return '';
      return 'CAROUSEL';
    }

    const { layout, menu_type } = behaviorSelected;
    if (menu_type === 'second_menu' && childs && layout === 'nolayout') {
      return 'DROPDOWN';
    }
    if (menu_type === 'second_menu' && childs) {
      return 'CAROUSEL';
    }
    return '';
  };

  const getNodesDropDown = () =>
    nodesSelected.childs.map((m, index) => ({
      id: index,
      value: m.code,
      label: m.text,
    }));

  const navigateToGuideChannels = (node, retryCount = 0) => {
    const MAX_RETRIES = 20; // máximo 20 intentos
    const RETRY_DELAY_MS = 500; // esperar 500ms entre intentos
    // Este defaultLiveChannel se setea cuando entramos al Player
    const defaultLiveChannel = localStorage.getItem('defaultLiveChannel');
    const currentEpgLineup = epgLineupRef.current;

    // Si no hay lineup ni canal por defecto, reintentamos con delay
    if (currentEpgLineup.length === 0 && !defaultLiveChannel) {
      if (retryCount >= MAX_RETRIES) {
        return;
      }
      setTimeout(
        () => navigateToGuideChannels(node, retryCount + 1),
        RETRY_DELAY_MS,
      );
      return;
    }

    // verificamos si hay un canal por defecto desde la epg/lineup
    const defaultChannelFromEpgLineup = currentEpgLineup?.find(
      channel => channel?.default === true,
    );

    const canPlayDefaultChannelFromEpgLineup =
      defaultChannelFromEpgLineup &&
      canPlay(defaultChannelFromEpgLineup?.group_id, infinite);

    // iteramos la epgLineup y tomamos el primer canal con play en 1
    const channelCanPlayFromEpgLineup = currentEpgLineup?.find(channel =>
      canPlay(channel?.group_id, infinite),
    );

    let groupIdToRedirect;
    let hasDefaultChannel = false;
    let canPlayDefaultChannel = false;

    if (defaultLiveChannel) {
      hasDefaultChannel = true;
      if (canPlay(defaultLiveChannel, infinite)) {
        groupIdToRedirect = defaultLiveChannel;
        canPlayDefaultChannel = true;
      }
    } else if (canPlayDefaultChannelFromEpgLineup) {
      groupIdToRedirect = defaultChannelFromEpgLineup?.group_id;
    } else if (channelCanPlayFromEpgLineup) {
      groupIdToRedirect = channelCanPlayFromEpgLineup?.group_id;
    }

    // verificamos si estamos en mobile
    if (Utils.MobileCheck()) {
      const goTo = ClaroUrlBuilder.mobileGoToApp(
        GetRegion(),
        groupIdToRedirect,
        false,
      );
      return document.location.assign(goTo);
    }

    // verificamos si hay default en localStorage
    if (hasDefaultChannel) {
      if (canPlayDefaultChannel) {
        return dispatch(goToPlayerWithId(defaultLiveChannel, navigate, { state: { isAutoChannelSelection: true } }));
      }
      // mostrar el modal de transacciones y remover el ultima canal visto
      dispatch(
        getPurchaseButtonInfoForEPGTransactions({
          group_id: defaultLiveChannel,
        }),
      );
      dispatch(showEpgTransactions(defaultLiveChannel));
      return localStorage.removeItem('defaultLiveChannel');
    }

    dispatch(goToPlayerWithId(groupIdToRedirect, navigate, { state: { isAutoChannelSelection: true } }));
  };

  const navigateToChannel = (channel, pushToHistory) => {
    // verificamos si estamos en mobile

    if (Utils.MobileCheck()) {
      if (canPlay(channel?.id, infinite)) {
        const goTo = ClaroUrlBuilder.mobileGoToApp(
          GetRegion(),
          channel?.id,
          false,
        );
        return document.location.assign(goTo);
      }

      dispatch(
        getPurchaseButtonInfoForEPGTransactions({ group_id: channel.id }),
      );
      dispatch(showEpgTransactions(channel));
      return;
    }

    if (canPlay(channel?.id, infinite)) {
      if (pushToHistory?.value) {
        dispatch(goToPlayerWithId(channel?.id, navigate));
        return;
      }
      dispatch(goToPlayerWithId(channel?.id, navigate));
      return;
    }

    dispatch(getPurchaseButtonInfoForEPGTransactions({ group_id: channel.id }));
    dispatch(showEpgTransactions(channel));
  };

  const protectedMundiaNodeRedirect = () => {
    const currentCode = 'mundial2026';
    const searchParams = new URLSearchParams(window.location.search);
    const deviceModel = searchParams.get('device_model');
    const incomingUserhash =
      searchParams.get('user_hash') || searchParams.get('userhash');
    const storedUserhash = localStorage.getItem(LOCAL_STORAGE_USERHASH);

    if (
      !pathname.includes(currentCode) ||
      user?.userhash ||
      storedUserhash ||
      deviceModel ||
      incomingUserhash
    )
      return;

    const currentNode = nodes?.find(node => node?.code === currentCode);
    const appBehaviour = JSON.parse(currentNode?.app_behaviour || '{}');
    const enableAnonymous = appBehaviour?.node_config?.vis?.anon ?? 0;

    if (!enableAnonymous)
      return navigate(`/${getRegion()}/${getFirstNode()}`, { replace: true });
  };
  useEffect(
    () => {
      if (Object.keys(nodes).length === 0) return;
      // evita ejecutar redirects durante resize
      if (resizingRef.current) return;
      protectedMundiaNodeRedirect();
    },
    [nodes, user?.userhash, pathname, search],
  );

  useEffect(() => {
    let resizeDebounceTimeoutId = null;
    const onResize = () => {
      resizingRef.current = true;
      if (resizeDebounceTimeoutId) clearTimeout(resizeDebounceTimeoutId);
      resizeDebounceTimeoutId = setTimeout(() => {
        resizingRef.current = false;
      }, 250);
    };
    window.addEventListener('resize', onResize);
    return () => {
      if (resizeDebounceTimeoutId) clearTimeout(resizeDebounceTimeoutId);
      window.removeEventListener('resize', onResize);
    };
  }, []);

  return {
    nodes,
    nodesSelected,
    handleRedirect,
    getTypeSubmenu,
    getNodesDropDown,
    setInitialNode,
    navigateToChannel,
    firstNode,
  };
};
export default useMenuRedirect;
